import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { GameProvider } from './context/GameContext';
import AppLayout from './components/layout/AppLayout';
import ErrorBoundary from './components/layout/ErrorBoundary';
import LevelUpModal from './components/game/LevelUpModal';
import GameGuideOnboarding from './components/game/GameGuideOnboarding';

import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import LaunchSoonPage from './pages/LaunchSoonPage';
import CoursesPage from './pages/CoursesPage';
import CoursePage from './pages/CoursePage';
import ModulePage from './pages/ModulePage';
import ConceptPage from './pages/ConceptPage';
import KnowledgeVaultPage from './pages/KnowledgeVaultPage';
import ProfilePage from './pages/ProfilePage';
import LeaderboardPage from './pages/LeaderboardPage';
import AuthCallback from './pages/AuthCallback';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import GameGuidePage from './pages/GameGuidePage';
import SettingsPage from './pages/SettingsPage';
import NotFoundPage from './pages/NotFoundPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import MaintenancePage from './pages/MaintenancePage';
import { isLaunchModeEnabled } from './lib/config';

// Redirect helpers for dynamic routes
function CourseRedirect() {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/courses/${id}`} replace />;
}

function ModuleRedirect() {
    const { courseId, slug } = useParams<{ courseId: string; slug: string }>();
    return <Navigate to={`/courses/${courseId}/${slug}`} replace />;
}

function ConceptRedirect() {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/armory/${id}`} replace />;
}

function LaunchModeApp() {
    return (
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AuthProvider>
                <Routes>
                    <Route path="/" element={<LaunchSoonPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="*" element={<LaunchSoonPage />} />
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    );
}

function MainApp() {
    return (
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AuthProvider>
                <GameProvider>
                    <AppLayout>
                        <LevelUpModal />
                        <GameGuideOnboarding />
                        <Routes>
                            {/* Core pages */}
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/dashboard" element={<DashboardPage />} />

                            {/* Quests (courses) */}
                            <Route path="/courses" element={<CoursesPage />} />
                            <Route path="/courses/:id" element={<CoursePage />} />
                            <Route path="/courses/:courseId/:slug" element={<ModulePage />} />

                            {/* Armory (knowledge vault) */}
                            <Route path="/armory" element={<KnowledgeVaultPage />} />
                            <Route path="/armory/:id" element={<ConceptPage />} />

                            {/* Rankings */}
                            <Route path="/leaderboard" element={<LeaderboardPage />} />

                            {/* Player */}
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/settings" element={<SettingsPage />} />

                            {/* Standalone / utility */}
                            <Route path="/maintenance" element={<MaintenancePage />} />
                            <Route path="/403" element={<AccessDeniedPage />} />

                            {/* Legacy redirects */}
                            <Route path="/base-camp" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/grind" element={<Navigate to="/courses" replace />} />
                            <Route path="/grind/:id" element={<CourseRedirect />} />
                            <Route path="/grind/:courseId/:slug" element={<ModuleRedirect />} />
                            <Route path="/hall-of-fame" element={<Navigate to="/leaderboard" replace />} />
                            <Route path="/concepts" element={<Navigate to="/armory" replace />} />
                            <Route path="/concepts/:id" element={<ConceptRedirect />} />
                            <Route path="/knowledge-vault" element={<Navigate to="/armory" replace />} />
                            <Route path="/controls" element={<Navigate to="/settings" replace />} />
                            <Route path="/profile/settings" element={<Navigate to="/settings" replace />} />

                            {/* Static pages */}
                            <Route path="/auth/callback" element={<AuthCallback />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/game-guide" element={<GameGuidePage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                    </AppLayout>
                </GameProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default function App() {
    return (
        <ErrorBoundary>
            {isLaunchModeEnabled() ? <LaunchModeApp /> : <MainApp />}
        </ErrorBoundary>
    );
}
